import { useState, useEffect, useCallback, useRef } from "react";
import { paymentsApi, bereavementsApi, adminApi, adminToolsApi, usersApi, walletApi } from "@/lib/api";
import { format } from "date-fns";
import { toast } from "sonner";
import { CheckCircle, X, Receipt, Coin, UsersThree, UserCircle, MagnifyingGlass, ClockCountdown, ArrowsClockwise, Phone, XCircle, WarningCircle, Wallet } from "@phosphor-icons/react";

const SECTIONS = [
  { key: "pending", label: "Pending Approvals", icon: ClockCountdown },
  { key: "manual", label: "Record Manual", icon: Coin },
  { key: "deposit", label: "Deposit to Member", icon: Wallet },
  { key: "bulk", label: "Bulk by Bereavement", icon: UsersThree },
  { key: "byMember", label: "Bulk by Member", icon: UserCircle },
  { key: "recent", label: "Recent Activity", icon: Receipt },
  { key: "stk", label: "STK Attempts (IPN)", icon: Phone },
];

export default function PaymentsTab({ onRefresh }) {
  const [section, setSection] = useState("pending");
  const [pendingCount, setPendingCount] = useState(0);

  return (
    <div className="space-y-4" data-testid="admin-payments-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <Receipt className="w-5 h-5" /> Payments
          </h2>
          <p className="text-xs text-muted-foreground mt-1">Approve member-submitted payments, record manual entries, or reconcile bulk transactions.</p>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1 border-b border-border">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSection(s.key)}
            data-testid={`payments-section-${s.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-t-xl text-sm font-medium whitespace-nowrap transition-colors ${
              section === s.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            <s.icon className="w-4 h-4" weight={section === s.key ? "fill" : "regular"} />
            {s.label}
            {s.key === "pending" && pendingCount > 0 && (
              <span className={`ml-1 text-[10px] font-bold rounded-full px-1.5 py-0.5 ${section === s.key ? "bg-primary-foreground text-primary" : "bg-destructive text-destructive-foreground"}`}>{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {section === "pending" && <PendingPanel onRefresh={onRefresh} onCountChange={setPendingCount} />}
      {section === "manual" && <ManualPanel onRefresh={onRefresh} />}
      {section === "deposit" && <DepositToMemberPanel onRefresh={onRefresh} />}
      {section === "bulk" && <BulkPanel onRefresh={onRefresh} />}
      {section === "byMember" && <ByMemberPanel onRefresh={onRefresh} />}
      {section === "recent" && <RecentPanel />}
      {section === "stk" && <StkAttemptsPanel />}
    </div>
  );
}

// ─── Pending Approvals ────────────────────────────────────────────────────────
function PendingPanel({ onRefresh, onCountChange }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await paymentsApi.listPending();
      setItems(data);
      onCountChange(data.length);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to load"); }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const confirm = async (id) => {
    setBusyId(id);
    try {
      const { data } = await paymentsApi.confirm(id);
      toast.success(data.message || "Confirmed");
      load(); onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Why are you rejecting? (visible in audit log)") || "";
    if (reason === null) return;
    setBusyId(id);
    try {
      await paymentsApi.reject(id, reason);
      toast.success("Rejected");
      load(); onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  if (items === null) return <div className="space-y-2">{[1,2,3].map((i)=> <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />)}</div>;
  if (items.length === 0) return (
    <div className="text-center py-12 text-muted-foreground" data-testid="payments-no-pending">
      <CheckCircle className="w-10 h-10 mx-auto mb-2 text-muted-foreground/40" weight="duotone" />
      <p className="text-sm">No payments waiting for approval.</p>
    </div>
  );

  return (
    <div className="space-y-2" data-testid="payments-pending-list">
      {items.map((it) => (
        <div key={it.id} className="bg-card border border-border rounded-xl p-4" data-testid={`pending-payment-${it.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <p className="font-semibold text-sm">{it.bereavement?.deceasedName || "—"}</p>
              <p className="text-xs text-muted-foreground">
                {(it.members || []).map(m => `${m.name} (${m.ukc})`).join(", ")}
                {" · "}submitted by {it.submittedBy?.name || "—"}
              </p>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Tag label="Method" value={(it.paymentMethod || "—").toUpperCase()} />
                <Tag label="Reference" value={it.reference || "—"} mono />
                <Tag label="Amount" value={`KES ${Number(it.totalAmount || 0).toLocaleString()}`} />
                <Tag label="Paid at" value={it.paidAt ? format(new Date(it.paidAt), "dd MMM yy HH:mm") : "—"} />
              </div>
              {it.notes && <p className="text-[11px] mt-2 text-muted-foreground italic">"{it.notes}"</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => confirm(it.id)} disabled={busyId === it.id}
                data-testid={`confirm-payment-${it.id}`}
                className="px-3 py-2 rounded-xl bg-primary text-primary-foreground font-semibold text-xs active:scale-95 disabled:opacity-60 inline-flex items-center gap-1"
              >
                <CheckCircle className="w-4 h-4" weight="fill" /> Confirm
              </button>
              <button
                onClick={() => reject(it.id)} disabled={busyId === it.id}
                data-testid={`reject-payment-${it.id}`}
                className="px-3 py-2 rounded-xl bg-destructive/10 text-destructive border border-destructive/20 font-semibold text-xs active:scale-95 disabled:opacity-60 inline-flex items-center gap-1"
              >
                <X className="w-4 h-4" /> Reject
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Manual single-member payment ─────────────────────────────────────────────
function ManualPanel({ onRefresh }) {
  const [bereavements, setBereavements] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberHits, setMemberHits] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [form, setForm] = useState({
    bereavementId: "", memberId: "", memberLabel: "", method: "mpesa", reference: "", paidAt: new Date().toISOString().slice(0, 16), notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    bereavementsApi.list({ status: "active" }).then(({ data }) => setBereavements(data || [])).catch(() => setBereavements([]));
  }, []);

  useEffect(() => {
    if (memberQuery.trim().length < 2) { setMemberHits([]); return; }
    const id = setTimeout(async () => {
      setSearchBusy(true);
      try {
        const { data } = await adminApi.searchMembers(memberQuery.trim());
        setMemberHits(data.slice(0, 8));
      } catch { setMemberHits([]); }
      finally { setSearchBusy(false); }
    }, 250);
    return () => clearTimeout(id);
  }, [memberQuery]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bereavementId || !form.memberId) { toast.error("Select both bereavement and member"); return; }
    if (form.method !== "cash" && !form.reference.trim()) { toast.error("Reference is required"); return; }
    setBusy(true);
    try {
      await paymentsApi.manualPayment({
        bereavementId: form.bereavementId,
        memberId: form.memberId,
        paymentMethod: form.method,
        reference: form.reference.trim(),
        paidAt: form.paidAt ? new Date(form.paidAt).toISOString() : undefined,
        notes: form.notes.trim(),
      });
      toast.success("Payment recorded");
      setForm({ ...form, memberId: "", memberLabel: "", reference: "", notes: "" });
      setMemberQuery("");
      onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-xl p-5 space-y-3 max-w-2xl" data-testid="manual-payment-form">
      <p className="text-sm text-muted-foreground">Record a payment that didn't come through the app — e.g. cash handed to the treasurer, or an M-Pesa send the member made before the system had STK Push wired up.</p>

      <div>
        <label className="text-xs font-medium block mb-1">Bereavement</label>
        <select required value={form.bereavementId} onChange={(e) => setForm(f => ({ ...f, bereavementId: e.target.value }))} data-testid="manual-bereavement-select" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl">
          <option value="">— select —</option>
          {bereavements.map((b) => <option key={b.id} value={b.id}>{b.deceasedName} · KES {b.contributionAmount}</option>)}
        </select>
      </div>

      <div>
        <label className="text-xs font-medium block mb-1">Member</label>
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={form.memberLabel || memberQuery}
            onChange={(e) => { setMemberQuery(e.target.value); setForm(f => ({ ...f, memberId: "", memberLabel: "" })); }}
            placeholder="Search by name, UKC, or phone…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl"
            data-testid="manual-member-search"
          />
          {memberHits.length > 0 && !form.memberId && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
              {memberHits.map((m) => (
                <button
                  key={m.id} type="button"
                  onClick={() => { setForm(f => ({ ...f, memberId: m.id, memberLabel: `${m.name} (${m.ukcNumber || m.memberNumber})` })); setMemberHits([]); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-secondary border-b border-border last:border-b-0"
                  data-testid={`manual-member-hit-${m.id}`}
                >
                  <p className="font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.ukcNumber || m.memberNumber} · {m.phone}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <MethodAndRefRow form={form} setForm={setForm} />

      <div>
        <label className="text-xs font-medium block mb-1">Payment date & time</label>
        <input type="datetime-local" value={form.paidAt} onChange={(e) => setForm(f => ({ ...f, paidAt: e.target.value }))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" />
      </div>

      <div>
        <label className="text-xs font-medium block mb-1">Notes</label>
        <textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional context for the audit log" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl resize-none" />
      </div>

      <button type="submit" disabled={busy || !form.bereavementId || !form.memberId} data-testid="manual-payment-submit" className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-60">
        {busy ? "Recording…" : "Record payment"}
      </button>
    </form>
  );
}

// ─── Bulk Reconcile ───────────────────────────────────────────────────────────
function BulkPanel({ onRefresh }) {
  const [bereavements, setBereavements] = useState([]);
  const [bereavementId, setBereavementId] = useState("");
  const [method, setMethod] = useState("mpesa");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 16));
  const [notes, setNotes] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [memberHits, setMemberHits] = useState([]);
  const [selected, setSelected] = useState({}); // id → {name, ukc}
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    bereavementsApi.list({ status: "active" }).then(({ data }) => setBereavements(data || [])).catch(() => setBereavements([]));
  }, []);

  useEffect(() => {
    if (memberQuery.trim().length < 2) { setMemberHits([]); return; }
    const id = setTimeout(async () => {
      try {
        const { data } = await adminApi.searchMembers(memberQuery.trim());
        setMemberHits(data.filter(m => !selected[m.id]).slice(0, 10));
      } catch { setMemberHits([]); }
    }, 250);
    return () => clearTimeout(id);
  }, [memberQuery, selected]);

  const addMember = (m) => {
    setSelected(s => ({ ...s, [m.id]: { name: m.name, ukc: m.ukcNumber || m.memberNumber } }));
    setMemberQuery("");
    setMemberHits([]);
  };

  const removeMember = (id) => setSelected(s => { const next = { ...s }; delete next[id]; return next; });

  const selectedIds = Object.keys(selected);
  const ber = bereavements.find(b => b.id === bereavementId);
  const totalEstimate = ber ? (ber.contributionAmount || 200) * selectedIds.length : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!bereavementId) { toast.error("Pick a bereavement"); return; }
    if (selectedIds.length === 0) { toast.error("Add at least one member"); return; }
    if (method !== "cash" && !reference.trim()) { toast.error("Reference required"); return; }
    setBusy(true);
    try {
      const { data } = await paymentsApi.bulkPayment({
        bereavementId,
        memberIds: selectedIds,
        paymentMethod: method,
        reference: reference.trim(),
        paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
        notes: notes.trim(),
      });
      toast.success(data.message);
      setSelected({}); setReference(""); setNotes("");
      onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-xl p-5 space-y-3 max-w-2xl" data-testid="bulk-payment-form">
      <p className="text-sm text-muted-foreground">One M-Pesa or bank transaction covering many members at the same per-member amount. Each selected member gets a contribution row stamped with the same reference.</p>

      <div>
        <label className="text-xs font-medium block mb-1">Bereavement</label>
        <select required value={bereavementId} onChange={(e) => setBereavementId(e.target.value)} data-testid="bulk-bereavement-select" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl">
          <option value="">— select —</option>
          {bereavements.map((b) => <option key={b.id} value={b.id}>{b.deceasedName} · KES {b.contributionAmount} per member</option>)}
        </select>
      </div>

      <MethodAndRefRow
        form={{ method, reference }}
        setForm={(updater) => { const next = updater({ method, reference }); setMethod(next.method); setReference(next.reference); }}
      />

      <div>
        <label className="text-xs font-medium block mb-1">Payment date & time</label>
        <input type="datetime-local" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" />
      </div>

      <div>
        <label className="text-xs font-medium block mb-1">Members covered ({selectedIds.length})</label>
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={memberQuery}
            onChange={(e) => setMemberQuery(e.target.value)}
            placeholder="Search and add members…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl"
            data-testid="bulk-member-search"
          />
          {memberHits.length > 0 && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
              {memberHits.map((m) => (
                <button key={m.id} type="button" onClick={() => addMember(m)} data-testid={`bulk-member-hit-${m.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary border-b border-border last:border-b-0">
                  <p className="font-medium">{m.name}</p>
                  <p className="text-xs text-muted-foreground">{m.ukcNumber || m.memberNumber} · {m.phone}</p>
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedIds.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="bulk-selected-chips">
            {selectedIds.map((id) => (
              <span key={id} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-md" data-testid={`bulk-selected-${id}`}>
                {selected[id].name} · {selected[id].ukc}
                <button type="button" onClick={() => removeMember(id)} className="hover:bg-primary/20 rounded">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-medium block mb-1">Notes (optional)</label>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl resize-none" />
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <p className="text-sm">
          Total: <strong className="font-body">KES {totalEstimate.toLocaleString()}</strong>
          <span className="text-xs text-muted-foreground ml-2">({selectedIds.length} × KES {ber?.contributionAmount || 200})</span>
        </p>
        <button type="submit" disabled={busy || selectedIds.length === 0} data-testid="bulk-payment-submit" className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-60">
          {busy ? "Recording…" : `Record bulk payment`}
        </button>
      </div>
    </form>
  );
}

// ─── Recent confirmed activity ────────────────────────────────────────────────
function RecentPanel() {
  const [data, setData] = useState(null);
  const load = useCallback(() => paymentsApi.recent().then(({ data }) => setData(data)).catch(() => setData({ recent: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (data === null) return <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-12 bg-muted rounded-xl animate-pulse" />)}</div>;
  return (
    <div data-testid="payments-recent-list">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-muted-foreground">Last 50 confirmed payments across the platform</p>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="payments-recent-refresh">
          <ArrowsClockwise className="w-3.5 h-3.5" weight="bold" /> Refresh
        </button>
      </div>
      <div className="bg-card border border-border rounded-xl divide-y divide-border overflow-hidden">
        {data.recent.length === 0 ? (
          <p className="text-center py-8 text-sm text-muted-foreground">No confirmed payments yet.</p>
        ) : data.recent.map((r) => (
          <div key={r.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{r.memberName}</p>
              <p className="text-xs text-muted-foreground">{r.deceasedName} · {r.method?.toUpperCase()} {r.reference && `· ${r.reference}`}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-semibold text-sm font-body">KES {Number(r.amount || 0).toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground">{r.paidAt ? format(new Date(r.paidAt), "dd MMM yy") : ""}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Shared bits ──────────────────────────────────────────────────────────────
function MethodAndRefRow({ form, setForm }) {
  const m = form.method;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="text-xs font-medium block mb-1">Payment method</label>
        <select value={m} onChange={(e) => setForm(f => ({ ...f, method: e.target.value }))} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="payment-method-select">
          <option value="mpesa">M-Pesa</option>
          <option value="bank">Bank</option>
          <option value="cash">Cash</option>
          <option value="cheque">Cheque</option>
        </select>
      </div>
      {m !== "cash" && (
        <div>
          <label className="text-xs font-medium block mb-1">Reference {m === "mpesa" ? "code" : "number"}</label>
          <input value={form.reference} onChange={(e) => setForm(f => ({ ...f, reference: e.target.value.toUpperCase() }))} placeholder={m === "mpesa" ? "RGA8K2X9LM" : "Slip / RTGS ref"} className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl font-mono" data-testid="payment-reference-input" required />
        </div>
      )}
    </div>
  );
}

// ─── Bulk by Member ──────────────────────────────────────────────────────────
function ByMemberPanel({ onRefresh }) {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);  // {id, name, memberNumber, phone}
  const [arrears, setArrears] = useState(null);   // {arrears:[], registrationFee:{paid,amount}, totalOwed}
  const [loading, setLoading] = useState(false);
  const [checkedBids, setCheckedBids] = useState(new Set());
  const [payRegFee, setPayRegFee] = useState(false);
  const [busy, setBusy] = useState(false);
  const formRef = useRef({
    paymentMethod: "mpesa",
    reference: "",
    paidAt: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  useEffect(() => {
    usersApi.getMembers()
      .then(({ data }) => setMembers(Array.isArray(data) ? data : (data?.members || [])))
      .catch(() => {});
  }, []);

  const pickMember = async (m) => {
    setSelected(m);
    setLoading(true);
    setArrears(null);
    setCheckedBids(new Set());
    setPayRegFee(false);
    try {
      const { data } = await adminToolsApi.memberArrears(m.id);
      setArrears(data);
      // Default: select ALL outstanding (matches the sweep-arrears UX)
      setCheckedBids(new Set((data.arrears || []).map(r => r.bereavementId)));
      setPayRegFee(!data.registrationFee?.paid);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load arrears");
    } finally {
      setLoading(false);
    }
  };

  const toggleBid = (bid) => {
    setCheckedBids(prev => {
      const next = new Set(prev);
      next.has(bid) ? next.delete(bid) : next.add(bid);
      return next;
    });
  };

  const computedTotal = (() => {
    if (!arrears) return 0;
    let t = 0;
    for (const r of arrears.arrears || []) {
      if (checkedBids.has(r.bereavementId)) t += r.contributionAmount;
    }
    if (payRegFee && !arrears.registrationFee?.paid) t += arrears.registrationFee.amount;
    return t;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const f = formRef.current;
    if (!selected) return;
    if (checkedBids.size === 0 && !payRegFee) {
      toast.error("Select at least one bereavement or the registration fee");
      return;
    }
    if (f.paymentMethod !== "cash" && !f.reference.trim()) {
      toast.error("Reference number is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminToolsApi.bulkByMember({
        memberId: selected.id,
        bereavementIds: Array.from(checkedBids),
        payRegistrationFee: payRegFee && !arrears?.registrationFee?.paid,
        paymentMethod: f.paymentMethod,
        reference: f.reference,
        paidAt: f.paidAt,
        notes: f.notes,
      });
      toast.success(data.message || "Recorded");
      onRefresh?.();
      // Reset to start of flow
      setSelected(null);
      setArrears(null);
      setCheckedBids(new Set());
      setPayRegFee(false);
      formRef.current = { paymentMethod: "mpesa", reference: "", paidAt: new Date().toISOString().slice(0, 10), notes: "" };
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record payment");
    } finally {
      setBusy(false);
    }
  };

  // ── Step 1: Pick member ─────────────────────────────────────────────────
  if (!selected) {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? members.filter(m =>
          (m.name || "").toLowerCase().includes(q) ||
          (m.memberNumber || m.ukcNumber || "").toLowerCase().includes(q) ||
          (m.phone || "").includes(q)
        )
      : members.slice(0, 30);
    return (
      <div className="space-y-3" data-testid="bymember-pick">
        <p className="text-sm text-muted-foreground">Select a member to view and pay their outstanding contributions and registration fee.</p>
        <div className="relative">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, UKC, or phone…"
            data-testid="bymember-search"
            className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-border max-h-[480px] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground text-center">No members match.</p>
          ) : filtered.map(m => (
            <button
              key={m.id}
              onClick={() => pickMember(m)}
              data-testid={`bymember-row-${m.id}`}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-secondary/40 transition-colors text-left"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{m.name}</p>
                <p className="text-[11px] text-muted-foreground truncate">{m.memberNumber || m.ukcNumber} · {m.phone} · {m.status}</p>
              </div>
              <span className="text-xs text-primary font-semibold whitespace-nowrap">View →</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Step 2: Outstanding list + form ─────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="bymember-form">
      <div className="flex items-center justify-between gap-2 bg-secondary/40 border border-border rounded-xl p-3">
        <div>
          <p className="font-semibold text-sm">{selected.name}</p>
          <p className="text-[11px] text-muted-foreground">{selected.memberNumber || selected.ukcNumber} · {selected.phone}</p>
        </div>
        <button onClick={() => { setSelected(null); setArrears(null); }} className="text-xs text-primary font-semibold">← Change member</button>
      </div>

      {loading ? (
        <p className="text-center text-sm text-muted-foreground py-8">Loading arrears…</p>
      ) : !arrears ? null : (
        <>
          <div className="bg-card border border-border rounded-xl divide-y divide-border" data-testid="bymember-list">
            {!arrears.registrationFee.paid && (
              <label className="px-4 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-secondary/30">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={payRegFee}
                    onChange={() => setPayRegFee(p => !p)}
                    data-testid="bymember-regfee-checkbox"
                    className="w-4 h-4"
                  />
                  <div>
                    <p className="text-sm font-medium">Registration fee</p>
                    <p className="text-[11px] text-muted-foreground">One-off — required for new members</p>
                  </div>
                </div>
                <p className="text-sm font-bold font-body">KES {arrears.registrationFee.amount.toLocaleString()}</p>
              </label>
            )}
            {(arrears.arrears || []).length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground text-center">No outstanding bereavement contributions.</p>
            ) : (arrears.arrears.map(r => (
              <label key={r.bereavementId} className="px-4 py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-secondary/30">
                <div className="flex items-center gap-3 min-w-0">
                  <input
                    type="checkbox"
                    checked={checkedBids.has(r.bereavementId)}
                    onChange={() => toggleBid(r.bereavementId)}
                    data-testid={`bymember-bid-${r.bereavementId}`}
                    className="w-4 h-4"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{r.deceasedName} <span className="text-muted-foreground font-normal">— {r.relationship}</span></p>
                    <p className="text-[11px] text-muted-foreground">{(r.dateOfDeath || "").slice(0, 10)}</p>
                  </div>
                </div>
                <p className="text-sm font-bold font-body whitespace-nowrap">KES {r.contributionAmount.toLocaleString()}</p>
              </label>
            )))}
          </div>

          <div className="flex items-center justify-between gap-2 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
            <p className="text-sm font-semibold">Total</p>
            <p className="font-heading text-xl font-bold text-primary" data-testid="bymember-total">KES {computedTotal.toLocaleString()}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3" data-testid="bymember-pay-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium block mb-1">Payment method</label>
                <select
                  defaultValue="mpesa"
                  onChange={(e) => { formRef.current.paymentMethod = e.target.value; }}
                  data-testid="bymember-method"
                  className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"
                >
                  <option value="mpesa">M-Pesa</option>
                  <option value="bank">Bank</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Reference (shared across all rows)</label>
                <input
                  type="text"
                  defaultValue=""
                  onChange={(e) => { formRef.current.reference = e.target.value; }}
                  placeholder="QGH1234XYZ"
                  data-testid="bymember-reference"
                  className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Paid at</label>
                <input
                  type="date"
                  defaultValue={formRef.current.paidAt}
                  onChange={(e) => { formRef.current.paidAt = e.target.value; }}
                  data-testid="bymember-paidat"
                  className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Notes (optional)</label>
                <input
                  type="text"
                  defaultValue=""
                  onChange={(e) => { formRef.current.notes = e.target.value; }}
                  placeholder="Cash collected at meeting"
                  data-testid="bymember-notes"
                  className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy || computedTotal === 0}
              data-testid="bymember-submit"
              className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-50"
            >
              {busy ? "Recording…" : `Record KES ${computedTotal.toLocaleString()} payment`}
            </button>
          </form>
        </>
      )}
    </div>
  );
}


function Tag({ label, value, mono }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</p>
      <p className={`${mono ? "font-mono" : ""} text-foreground truncate`}>{value}</p>
    </div>
  );
}


// ─── STK Attempts (real-time IPN inspector) ───────────────────────────────────
function StkAttemptsPanel() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rail, setRail] = useState("");     // "" | "mpesa" | "lks"
  const [status, setStatus] = useState(""); // "" | "success" | "failed" | "pending"
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.listStkPayments({
        rail: rail || undefined,
        status: status || undefined,
        query: query || undefined,
        limit: 150,
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load STK attempts");
    } finally {
      setLoading(false);
    }
  }, [rail, status, query]);

  useEffect(() => {
    setLoading(true);
    load();
    // Auto-refresh every 15s so admins see IPNs land in near-real-time
    timerRef.current = setInterval(load, 15000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [load]);

  const statusPill = (s) => {
    if (s === "success") return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (s === "failed")  return "bg-red-100 text-red-800 border-red-200";
    return "bg-amber-100 text-amber-800 border-amber-200";
  };
  const statusIcon = (s) => {
    if (s === "success") return <CheckCircle className="w-3.5 h-3.5" weight="fill" />;
    if (s === "failed")  return <XCircle className="w-3.5 h-3.5" weight="fill" />;
    return <WarningCircle className="w-3.5 h-3.5" weight="fill" />;
  };

  return (
    <div className="space-y-3" data-testid="stk-attempts-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search phone / receipt / checkoutRequestId / thirdPartyTrxId"
            data-testid="stk-attempts-search"
            className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl"
          />
        </div>
        <select value={rail} onChange={(e) => setRail(e.target.value)} className="px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="stk-attempts-rail">
          <option value="">All rails</option>
          <option value="mpesa">M-Pesa</option>
          <option value="lks">LKS</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 text-sm bg-card border border-input rounded-xl" data-testid="stk-attempts-status">
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
        </select>
        <button onClick={load} className="px-3 py-2 rounded-xl bg-secondary text-secondary-foreground text-sm inline-flex items-center gap-1.5" data-testid="stk-attempts-refresh">
          <ArrowsClockwise className="w-4 h-4" /> Refresh
        </button>
      </div>
      <p className="text-[11px] text-muted-foreground">Auto-refreshes every 15s. Click a row for the raw IPN payload.</p>

      {loading ? (
        <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />)}</div>
      ) : (items || []).length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-10 border border-dashed border-border rounded-xl">No STK attempts match this filter yet.</div>
      ) : (
        <div className="bg-card border border-border rounded-xl divide-y divide-border overflow-hidden">
          {items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => setSelected(it)}
              data-testid={`stk-attempt-${it.id}`}
              className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-secondary/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${statusPill(it.status)}`}>
                    {statusIcon(it.status)} {it.status}
                  </span>
                  <span className="text-[10px] uppercase bg-secondary px-1.5 py-0.5 rounded font-semibold">{it.rail}</span>
                  <span className="text-[10px] text-muted-foreground">{it.purpose}</span>
                </div>
                <p className="text-sm mt-1 truncate">
                  <span className="font-mono">{it.callbackPhone || it.phone || "—"}</span>
                  {" · "}
                  <span className="font-body font-semibold">KES {(it.totalAmount ?? it.amountPerMember ?? 0).toLocaleString()}</span>
                  {it.mpesaReceiptNumber && <> · <span className="font-mono text-emerald-700">{it.mpesaReceiptNumber}</span></>}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                  {it.resultDesc || "—"}
                  {it.checkoutRequestId && <> · <span className="font-mono">{it.checkoutRequestId}</span></>}
                </p>
              </div>
              <div className="text-right shrink-0 text-[11px] text-muted-foreground">
                <p>{it.createdAt ? format(new Date(it.createdAt), "dd MMM HH:mm") : "—"}</p>
                {it.callbackReceivedAt && <p className="text-emerald-700">IPN {format(new Date(it.callbackReceivedAt), "HH:mm:ss")}</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <StkAttemptDetail item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function StkAttemptDetail({ item, onClose }) {
  const [detail, setDetail] = useState(item);
  useEffect(() => {
    adminApi.getStkPayment(item.id).then(({ data }) => setDetail({ ...item, ...data })).catch(() => {});
  }, [item]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="stk-attempt-detail">
      <div className="bg-card w-full sm:max-w-2xl sm:rounded-xl rounded-t-2xl border border-border max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold">STK Attempt Detail</h3>
            <p className="text-xs text-muted-foreground">Rail: {detail.rail?.toUpperCase()} · Status: {detail.status}</p>
          </div>
          <button onClick={onClose} data-testid="stk-attempt-close" className="text-muted-foreground"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs bg-secondary/40 border border-border rounded-xl p-3">
          <Tag label="Phone" value={detail.callbackPhone || detail.phone || "—"} mono />
          <Tag label="Amount" value={`KES ${(detail.totalAmount ?? detail.amountPerMember ?? 0).toLocaleString()}`} />
          <Tag label="Receipt / TransID" value={detail.mpesaReceiptNumber || "—"} mono />
          <Tag label="ThirdPartyTrxId" value={detail.thirdPartyTrxId || "—"} mono />
          <Tag label="CheckoutRequestID" value={detail.checkoutRequestId || "—"} mono />
          <Tag label="MerchantRequestID" value={detail.merchantRequestId || "—"} mono />
          <Tag label="ResultCode" value={detail.resultCode ?? "—"} mono />
          <Tag label="TransactionDate" value={detail.transactionDate || "—"} mono />
          <Tag label="Purpose" value={detail.purpose || "—"} />
          <Tag label="Sent at" value={detail.createdAt ? format(new Date(detail.createdAt), "dd MMM yyyy HH:mm:ss") : "—"} />
          <Tag label="IPN received" value={detail.callbackReceivedAt ? format(new Date(detail.callbackReceivedAt), "dd MMM yyyy HH:mm:ss") : "— (no callback yet)"} />
        </div>

        <div>
          <p className="text-xs font-semibold mb-1">Result description</p>
          <p className="text-sm bg-card border border-border rounded-lg px-3 py-2">{detail.resultDesc || detail.responseDescription || "—"}</p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold">Raw IPN payload</p>
            {detail.callbackRaw && (
              <button
                onClick={() => { navigator.clipboard?.writeText(JSON.stringify(detail.callbackRaw, null, 2)); toast.success("Copied"); }}
                className="text-[11px] px-2 py-1 rounded bg-secondary text-secondary-foreground"
                data-testid="stk-attempt-copy-raw"
              >
                Copy JSON
              </button>
            )}
          </div>
          <pre className="bg-card border border-border rounded-lg px-3 py-2 text-[11px] font-mono overflow-x-auto max-h-72 whitespace-pre-wrap break-all">
            {detail.callbackRaw ? JSON.stringify(detail.callbackRaw, null, 2) : "— No IPN payload yet."}
          </pre>
        </div>
      </div>
    </div>
  );
}


// ─── Deposit to Member (unattached payment → wallet + auto-allocate) ─────────
function DepositToMemberPanel({ onRefresh }) {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Uncontrolled inputs (avoids React modal focus-loss bug)
  const amountRef = useRef(null);
  const methodRef = useRef(null);
  const refRef = useRef(null);
  const paidAtRef = useRef(null);
  const noteRef = useRef(null);
  const [autoAllocate, setAutoAllocate] = useState(true);
  const [payRegFee, setPayRegFee] = useState(false);
  const [enableAutoPay, setEnableAutoPay] = useState(false);

  useEffect(() => {
    usersApi.getMembers()
      .then(({ data }) => setMembers(Array.isArray(data) ? data : (data?.members || [])))
      .catch(() => {});
  }, []);

  const filtered = search.trim()
    ? members.filter((m) => {
        const q = search.trim().toLowerCase();
        return (
          (m.name || "").toLowerCase().includes(q) ||
          (m.memberNumber || m.ukcNumber || "").toLowerCase().includes(q) ||
          (m.phone || "").includes(q)
        );
      })
    : members.slice(0, 30);

  const pickMember = async (m) => {
    setSelected(m);
    setResult(null);
    setLoadingPreview(true);
    try {
      const { data } = await walletApi.adminArrearsPreview(m.id);
      setPreview(data);
      setPayRegFee(!data.member.registrationFeePaid);
      setEnableAutoPay(!!data.wallet.autoPayEnabled);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load member arrears");
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const clearSelection = () => {
    setSelected(null);
    setPreview(null);
    setResult(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    const amount = parseFloat(amountRef.current?.value || "0");
    const method = methodRef.current?.value || "cash";
    const reference = (refRef.current?.value || "").trim();
    const paidAt = paidAtRef.current?.value;
    const note = (noteRef.current?.value || "").trim();
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    if (method !== "cash" && !reference) { toast.error("Reference required for non-cash deposits"); return; }
    setBusy(true);
    try {
      const { data } = await walletApi.adminManualDeposit(selected.id, {
        amount,
        paymentMethod: method,
        reference,
        paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
        note,
        autoAllocate,
        payRegFeeFirst: payRegFee,
        enableAutoPay,
      });
      setResult(data);
      toast.success(`Deposited KES ${data.credited.toLocaleString()} · ${data.allocationCount} bereavement(s) cleared · KES ${data.surplusInWallet.toLocaleString()} surplus`);
      onRefresh?.();
      const { data: fresh } = await walletApi.adminArrearsPreview(selected.id);
      setPreview(fresh);
      if (amountRef.current) amountRef.current.value = "";
      if (refRef.current) refRef.current.value = "";
      if (noteRef.current) noteRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deposit failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="deposit-to-member-panel">
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 text-xs flex items-start gap-2">
        <Wallet className="w-4 h-4 mt-0.5 shrink-0 text-primary" weight="duotone" />
        <div className="text-foreground/90">
          <p className="font-semibold text-foreground">Unattached deposit — auto-allocated</p>
          <p className="text-muted-foreground mt-0.5">Credit a member with any amount (M-Pesa / bank / cash) and let the system clear their unpaid bereavements automatically (oldest first). Overpayment stays in their wallet, and you can enrol them into auto-pay for future events.</p>
        </div>
      </div>

      {!selected ? (
        <div>
          <div className="relative">
            <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, UKC number, or phone…"
              data-testid="deposit-member-search"
              className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-input rounded-xl"
            />
          </div>
          <div className="mt-3 bg-card border border-border rounded-xl divide-y divide-border max-h-[420px] overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">No matching members</p>
            ) : filtered.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => pickMember(m)}
                data-testid={`deposit-pick-${m.id}`}
                className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-secondary/40 transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{m.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{m.memberNumber || m.ukcNumber} · {m.phone}</p>
                </div>
                <span className="text-[10px] uppercase bg-secondary px-1.5 py-0.5 rounded font-semibold shrink-0">{m.status}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="deposit-form">
          <div className="flex items-center justify-between bg-secondary/40 border border-border rounded-xl px-4 py-2.5">
            <div className="min-w-0">
              <p className="font-semibold text-sm truncate">{selected.name}</p>
              <p className="text-[11px] text-muted-foreground truncate">{selected.memberNumber || selected.ukcNumber} · {selected.phone}</p>
            </div>
            <button type="button" onClick={clearSelection} className="text-xs px-2 py-1 rounded bg-secondary text-secondary-foreground" data-testid="deposit-change-member">Change</button>
          </div>

          {loadingPreview ? (
            <div className="h-16 bg-muted animate-pulse rounded-xl" />
          ) : preview && (
            <>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-card border border-border rounded-lg p-3 text-center">
                  <p className="text-lg font-bold font-body text-primary">KES {preview.wallet.balance.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">Wallet balance</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-3 text-center">
                  <p className="text-lg font-bold font-body text-accent">KES {preview.totalUnpaid.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">Outstanding · {preview.unpaidBereavements.length} events</p>
                </div>
                <div className="bg-card border border-border rounded-lg p-3 text-center">
                  <p className="text-lg font-bold font-body text-foreground">{preview.wallet.autoPayEnabled ? "ON" : "OFF"}</p>
                  <p className="text-[10px] text-muted-foreground">Auto-pay</p>
                </div>
              </div>
              {(preview.closedUnpaidCount || 0) > 0 && (
                <p className="text-[11px] text-muted-foreground -mt-1">
                  Includes <b>{preview.activeUnpaidCount}</b> active (KES {preview.activeUnpaidAmount.toLocaleString()}) &middot; <b>{preview.closedUnpaidCount}</b> historical/closed (KES {preview.closedUnpaidAmount.toLocaleString()}) — closed events are only settleable through admin deposit, not member self-sweep.
                </p>
              )}
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">Amount (KES) *</label>
              <input ref={amountRef} type="number" min="1" step="1" required data-testid="deposit-amount" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" placeholder="e.g. 1000" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Payment method *</label>
              <select ref={methodRef} defaultValue="mpesa" data-testid="deposit-method" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl">
                <option value="mpesa">M-Pesa</option>
                <option value="bank">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Reference / receipt no.</label>
              <input ref={refRef} type="text" data-testid="deposit-reference" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl font-mono" placeholder="M-Pesa code, slip #, cheque #…" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Date paid</label>
              <input ref={paidAtRef} type="date" defaultValue={new Date().toISOString().slice(0, 10)} data-testid="deposit-paid-at" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Note (optional)</label>
            <input ref={noteRef} type="text" data-testid="deposit-note" className="w-full px-3 py-2 text-sm bg-card border border-input rounded-xl" placeholder="e.g. Cash handed to treasurer at Sunday meeting" />
          </div>

          <div className="bg-card border border-border rounded-xl p-3 space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={autoAllocate} onChange={(e) => setAutoAllocate(e.target.checked)} data-testid="deposit-auto-allocate" className="w-4 h-4 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-medium">Auto-allocate to unpaid bereavements (oldest first)</p>
                <p className="text-[11px] text-muted-foreground">Sweeps through {preview?.unpaidBereavements.length || 0} unpaid event(s) until the amount is exhausted.</p>
              </div>
            </label>

            {preview && !preview.member.registrationFeePaid && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={payRegFee} onChange={(e) => setPayRegFee(e.target.checked)} data-testid="deposit-pay-regfee" className="w-4 h-4 mt-0.5" />
                <div className="text-sm flex-1">
                  <p className="font-medium">Settle registration fee first (KES {preview.member.registrationFeeAmount})</p>
                  <p className="text-[11px] text-muted-foreground">Deducted before allocating to bereavements.</p>
                </div>
              </label>
            )}

            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={enableAutoPay} onChange={(e) => setEnableAutoPay(e.target.checked)} data-testid="deposit-enable-autopay" className="w-4 h-4 mt-0.5" />
              <div className="text-sm flex-1">
                <p className="font-medium">Enrol into auto-pay for future bereavements</p>
                <p className="text-[11px] text-muted-foreground">Any surplus in the wallet will automatically pay new bereavements as they are posted.</p>
              </div>
            </label>
          </div>

          {preview?.unpaidBereavements?.length > 0 && autoAllocate && (
            <details className="bg-card border border-border rounded-xl overflow-hidden">
              <summary className="px-4 py-2.5 text-xs font-medium cursor-pointer hover:bg-secondary/40" data-testid="deposit-allocation-preview-toggle">
                Allocation preview · oldest {preview.unpaidBereavements.length} events
              </summary>
              <div className="divide-y divide-border max-h-56 overflow-y-auto">
                {preview.unpaidBereavements.map((b) => (
                  <div key={b.id} className="px-4 py-2 flex items-center justify-between text-xs">
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {b.deceasedName}
                        {b.status && b.status !== "active" && (
                          <span className="ml-2 text-[9px] uppercase bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded font-semibold">Closed</span>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground">{b.relationship} · {(b.dateOfDeath || "").slice(0, 10)}</p>
                    </div>
                    <span className="font-body font-semibold whitespace-nowrap">KES {b.contributionAmount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs space-y-1" data-testid="deposit-result">
              <p className="font-semibold text-emerald-900">Deposit successful</p>
              <p>Credited <b>KES {result.credited.toLocaleString()}</b> · cleared <b>{result.allocationCount}</b> bereavement(s){result.registrationFeeSettled ? " + registration fee" : ""} · surplus <b>KES {result.surplusInWallet.toLocaleString()}</b> in wallet.</p>
              <p className="text-muted-foreground">Wallet balance: KES {result.walletBalance.toLocaleString()} · Auto-pay: {result.autoPayEnabled ? "ON" : "OFF"}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !selected}
            data-testid="deposit-submit"
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Wallet className="w-4 h-4" weight="fill" />
            {busy ? "Recording…" : "Deposit & Auto-Allocate"}
          </button>
        </form>
      )}
    </div>
  );
}
